import { AuthUserSession } from '@lib/types/authUser.types';
import { EverysetUser } from '@lib/types/user.types';

export type ClaritySessionIdentity = {
  userId: number;
  impersonatedUserId: number | null;
  email: string | null;
};

/** Castifi id of the Cognito-authenticated user, never the impersonated one. */
const getSessionUserId = (session?: AuthUserSession | null): number | null => {
  const rawId =
    session?.attributes?.['custom:castifi_user_id'] ??
    session?.signInUserSession?.idToken?.payload?.['custom:castifi_user_id'];
  const userId = rawId != null ? Number(rawId) : Number.NaN;

  return Number.isFinite(userId) ? userId : null;
};

const toFiniteUserId = (
  value: number | string | null | undefined
): number | null => {
  if (value == null || value === '') return null;

  const userId = Number(value);

  return Number.isFinite(userId) ? userId : null;
};

const toNonEmptyEmail = (value: unknown): string | null => {
  if (typeof value !== 'string' || value === '') return null;

  return value;
};

/**
 * Resolves the session user's email only. Prefer Cognito attributes (always the
 * person at the keyboard); fall back to Redux profile.email only when that
 * profile belongs to the session user — never the impersonated target.
 */
export function resolveSessionUserEmail(
  session: AuthUserSession | null,
  profile: EverysetUser | null | undefined,
  sessionUserId: number
): string | null {
  const sessionEmail = toNonEmptyEmail(session?.attributes?.email);

  if (sessionEmail != null) return sessionEmail;

  if (profile != null && Number(profile.id) === sessionUserId) {
    return toNonEmptyEmail(profile.email);
  }

  return null;
}

/**
 * Age-gate fields from Redux `state.app.user.IUserInfoDAO` (UserProfile):
 * `about.birthday` and top-level `is_minor`. Not on `state.app.user.profile`
 * (EverysetUser). Missing fields return null / undefined — fail-open.
 */
export type ClarityAgeSignals = {
  birthday: string | null;
  isMinor: boolean | undefined;
};

export function resolveAgeSignalsFromUserProfile(
  profileInfo: UserProfile | null | undefined
): ClarityAgeSignals {
  const birthday = profileInfo?.about?.birthday;

  return {
    birthday: birthday == null || birthday === '' ? null : birthday,
    isMinor: profileInfo?.is_minor,
  };
}

/**
 * True when Redux UserProfile belongs to the given user id (session user).
 * During impersonation IUserInfoDAO holds the target, so age signals must come
 * from a dedicated session-user fetch instead.
 */
export function isUserProfileForUser(
  profileInfo: UserProfile | null | undefined,
  userId: number
): boolean {
  return profileInfo != null && Number(profileInfo.id) === userId;
}

/**
 * Resolves who Clarity records under, or null when there is no session user id.
 *
 * Sessions are always attributed to the Cognito-authenticated user — the person
 * at the keyboard. During impersonation, Redux may hold an impersonated target;
 * recording continues under the session user and the target is returned
 * separately for tagging when an explicit impersonation signal exists.
 *
 * Missing Redux profile or birthday must not block identity resolution
 * (fail-open). Age-gating waits separately for profile data to settle.
 */
export function resolveClaritySessionIdentity(
  session: AuthUserSession | null,
  profile: EverysetUser | null | undefined,
  impersonatedUserId: number | null = null
): ClaritySessionIdentity | null {
  const sessionUserId = getSessionUserId(session);

  if (sessionUserId == null) return null;

  const trustedImpersonatedId = toFiniteUserId(impersonatedUserId);
  const email = resolveSessionUserEmail(session, profile, sessionUserId);

  return {
    userId: sessionUserId,
    impersonatedUserId: trustedImpersonatedId,
    email,
  };
}

/**
 * Resolves the user Clarity attributes sessions to, or null when there is no
 * session user. Prefer {@link resolveClaritySessionIdentity} when callers also
 * need the impersonated target.
 */
export function resolveClarityUserId(
  session: AuthUserSession | null,
  profile: EverysetUser | null | undefined,
  impersonatedUserId: number | null = null
): number | null {
  return (
    resolveClaritySessionIdentity(session, profile, impersonatedUserId)
      ?.userId ?? null
  );
}
